import { z } from 'zod'
import { templateFieldSchema } from './template'

// ===========================================
// Clause Categories
// ===========================================

export const clauseCategorySchema = z.enum(
  [
    'services',
    'grace_period',
    'rent_increase',
    'renewal',
    'pets',
    'modifications',
    'insurance',
    'utilities',
    'other',
  ],
  {
    required_error: 'Categoria es requerida',
  }
)

export type ClauseCategory = z.infer<typeof clauseCategorySchema>

// Spanish labels for categories
export const CLAUSE_CATEGORY_LABELS: Record<ClauseCategory, string> = {
  services: 'Servicios',
  grace_period: 'Periodo de Gracia',
  rent_increase: 'Incrementos',
  renewal: 'Renovacion',
  pets: 'Mascotas',
  modifications: 'Modificaciones',
  insurance: 'Seguros',
  utilities: 'Servicios Publicos',
  other: 'Otros',
}

// Category sort order for display
export const CLAUSE_CATEGORY_ORDER: ClauseCategory[] = [
  'services',
  'grace_period',
  'rent_increase',
  'renewal',
  'pets',
  'modifications',
  'insurance',
  'utilities',
  'other',
]

// ===========================================
// Contract Types for Clauses
// ===========================================

export const clauseContractTypeSchema = z.enum(['residential', 'commercial'], {
  required_error: 'Tipo de contrato es requerido',
})

// ===========================================
// Clause Schema
// ===========================================

export const clauseSchema = z.object({
  title: z.string().min(1, 'Titulo de clausula es requerido'),
  category: clauseCategorySchema,
  htmlContent: z.string().min(1, 'Contenido HTML es requerido'),
  description: z.string().min(1, 'Descripcion es requerida'),
  contractTypes: z
    .array(clauseContractTypeSchema)
    .min(1, 'Selecciona al menos un tipo de contrato'),
  fields: z.array(templateFieldSchema).optional().default([]),
})

export type ClauseFormData = z.infer<typeof clauseSchema>

// ===========================================
// Clause Filters Schema
// ===========================================

export const clauseFiltersSchema = z.object({
  category: clauseCategorySchema.optional(),
  contractType: clauseContractTypeSchema.optional(),
  isActive: z.coerce.boolean().optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(50),
})

export type ClauseFilters = z.infer<typeof clauseFiltersSchema>

// ===========================================
// Reorder Schema
// ===========================================

export const reorderClausesSchema = z.object({
  category: clauseCategorySchema,
  clauseIds: z.array(z.string()).min(1, 'Se requiere al menos una clausula'),
})

export type ReorderClausesData = z.infer<typeof reorderClausesSchema>

// ===========================================
// API Response Types
// ===========================================

export interface ClauseResponse {
  id: string
  title: string
  category: ClauseCategory
  htmlContent: string
  description: string
  contractTypes: string[]
  isActive: boolean
  sortOrder: number
  fields: Array<{
    key: string
    label: string
    type: string
    required: boolean
    source?: string
    options?: string[]
    default?: string | number
  }> | null
  createdAt: string
  updatedAt: string
}
